import { AuthToken, JWTToken, RefreshToken } from '../../models/tokens';
import { BaseAuthService, TokenType } from './base_auth.service';
import { Map } from '../../../../shared/core/types/map';
import jwt_decode from 'jwt-decode';

export class AuthService implements BaseAuthService {
    public authToken: JWTToken | null;
    public refreshToken: RefreshToken | null;
    public authSpayUser: string | AuthToken;

    public static accessTokenName: string = 'spay-access-token';
    public static refreshTokenName: string = 'spay-refresh-token';
    public static clientTokenName: string = 'spay-client-token';
    public static authSpayUserName: string = 'spay-auth-token';
    public static merchantIdName: string = 'spay-merchant-id';

    constructor() {
        this.authToken = this.getToken('access-token');
        this.refreshToken = this.getToken('refresh-token');
        this.authSpayUser = this.getToken('auth-spay-user');
    }

    private getTokenName(tokenType: TokenType): string {
        switch (tokenType) {
            case 'access-token':
                return AuthService.accessTokenName;
            case 'auth-spay-user':
                return AuthService.authSpayUserName;
            case 'merchant-id':
                return AuthService.merchantIdName;
            case 'client-token':
                return AuthService.clientTokenName;
            default:
                return AuthService.refreshTokenName;
        }
    }

    private normalizeAuthToken(raw: any): AuthToken | null {
        if (!raw) return null;
        if (raw.access_token) return raw as AuthToken;
        if (raw.data?.access_token) return raw.data as AuthToken;
        if (raw.result?.access_token) return raw.result as AuthToken;
        if (raw.token?.access_token) return raw.token as AuthToken;
        return null;
    }

    public getToken(tokenType: TokenType): JWTToken | RefreshToken | null {
        const tokenName: string = this.getTokenName(tokenType);
        const storedToken = localStorage.getItem(tokenName);

        console.log(`[AuthService] getToken(${tokenType}) from localStorage:`, !!storedToken);

        if (!!storedToken) {
            try {
                const parsed = JSON.parse(storedToken);
                console.log(`[AuthService] getToken(${tokenType}): JSON parse SUCCESS`);
                return parsed;
            } catch (e) {
                console.error(`[AuthService] getToken(${tokenType}): JSON parse FAILED`, e);
                return null;
            }
        }

        console.log(`[AuthService] getToken(${tokenType}): NOT FOUND`);
        return null;
    }

    public setToken(tokenType: TokenType, token: any): void {
        console.log(`[AuthService] setToken(${tokenType})`);
        const tokenName: string = this.getTokenName(tokenType);

        if (token === null) {
            console.log(`[AuthService] Removing token ${tokenType}`);
            localStorage.removeItem(tokenName);
            return;
        }

        try {
            const jsonStr = JSON.stringify(token);
            localStorage.setItem(tokenName, jsonStr);
            console.log(`[AuthService] setToken(${tokenType}) stored in localStorage, length:`, jsonStr.length);
        } catch (error) {
            console.error(`[AuthService] setToken(${tokenType}) error:`, error);
        }
    }

    public removeToken(tokenType: TokenType): void {
        console.log(`[AuthService] removeToken(${tokenType})`);
        const tokenName = this.getTokenName(tokenType);
        localStorage.removeItem(tokenName);
    }

    public getLoggedInUser(): AuthToken | string {
        const rawUser = this.getToken('auth-spay-user');
        console.log('[AuthService] getLoggedInUser - rawUser is null?', rawUser === null);
        if (rawUser) {
            console.log('[AuthService] getLoggedInUser - rawUser keys:', Object.keys(rawUser));
        }
        const user = this.normalizeAuthToken(rawUser);
        console.log('[AuthService] getLoggedInUser result:', user ? 'USER OBJECT FOUND' : 'NULL');
        if (user) {
            console.log('[AuthService] getLoggedInUser - normalized user keys:', Object.keys(user));
            console.log('[AuthService] getLoggedInUser - has access_token:', !!user.access_token);
        }
        return !!user ? user : null;
    }

    public isAuthenticated(): boolean {
        console.log('[AuthService] isAuthenticated check START');
        try {
            const user = this.getLoggedInUser() as AuthToken;

            if (!user) {
                console.log('[AuthService] isAuthenticated: user object is null');
                return false;
            }

            if (!user.access_token) {
                console.log('[AuthService] isAuthenticated: access_token is missing in user object', user);
                return false;
            }

            // Check if token is expired
            const decodedToken = jwt_decode(user.access_token) as Map;
            const currentTime = Date.now() / 1000; // Convert to seconds
            console.log('[AuthService] Token exp:', decodedToken.exp, 'Current time:', currentTime);

            if (decodedToken.exp && decodedToken.exp < currentTime) {
                console.warn('[AuthService] isAuthenticated: Token EXPIRED');
                this.removeToken('access-token');
                this.removeToken('refresh-token');
                this.removeToken('auth-spay-user');
                return false;
            }

            console.log('[AuthService] isAuthenticated: TRUE');
            return true;
        } catch (error) {
            console.error('[AuthService] isAuthenticated error:', error);
            return false;
        }
    }

    public userInfo(): any {
        try {
            const user = this.getLoggedInUser() as AuthToken;
            console.log('[AuthService] userInfo - getLoggedInUser result:', user ? 'USER FOUND' : 'NULL');
            if (!user) {
                console.warn('[AuthService] userInfo - user is null, cannot decode token');
                return null;
            }
            if (!user.access_token) {
                console.warn('[AuthService] userInfo - access_token is missing from user object');
                return null;
            }
            console.log('[AuthService] userInfo - decoding access_token');
            const decoded = jwt_decode(user.access_token);
            console.log('[AuthService] userInfo - decoded token keys:', Object.keys(decoded));
            return decoded;
        } catch (error) {
            console.error('[AuthService] userInfo error:', error);
            return null;
        }
    }

    public basicToken(): Map {
        const clientId = process.env.REACT_APP_CLIENT_ID || (typeof window !== 'undefined' && (window as any).env?.REACT_APP_CLIENT_ID) || 'client';
        const clientSecret = process.env.REACT_APP_CLIENT_SECRET || (typeof window !== 'undefined' && (window as any).env?.REACT_APP_CLIENT_SECRET) || 'pass';
        return {
            username: clientId,
            password: clientSecret
        };
    }

    public accessToken(): string {
        const user = this.getLoggedInUser() as AuthToken;

        if (this.isAuthenticated()) {
            return user.access_token;
        }

        return null;
    }

    public debugInfo(): any {
        console.log('[AuthService] ===== DEBUG INFO =====');
        console.log('[AuthService] localStorage.spay-auth-token exists:', !!localStorage.getItem('spay-auth-token'));
        console.log('[AuthService] localStorage.spay-access-token exists:', !!localStorage.getItem('spay-access-token'));
        
        const rawAuthToken = localStorage.getItem('spay-auth-token');
        if (rawAuthToken) {
            console.log('[AuthService] spay-auth-token length:', rawAuthToken.length);
            console.log('[AuthService] spay-auth-token is encrypted (starts with U2FsdGVk):', rawAuthToken.startsWith('U2FsdGVk'));
            console.log('[AuthService] spay-auth-token is JSON (starts with {):', rawAuthToken.startsWith('{'));
        }
        
        const loggedInUser = this.getLoggedInUser();
        console.log('[AuthService] getLoggedInUser result:', loggedInUser ? 'HAS VALUE' : 'NULL');
        if (loggedInUser && typeof loggedInUser === 'object') {
            console.log('[AuthService] loggedInUser.access_token:', (loggedInUser as any).access_token ? 'EXISTS' : 'MISSING');
        }
        
        return {
            hasAuthToken: !!localStorage.getItem('spay-auth-token'),
            hasAccessToken: !!localStorage.getItem('spay-access-token'),
            authTokenLength: rawAuthToken?.length || 0,
            isEncrypted: rawAuthToken?.startsWith('U2FsdGVk') || false,
            hasLoggedInUser: !!loggedInUser,
            isAuthenticated: this.isAuthenticated()
        };
    }
}
